import { Box, Paper, Text } from "@mantine/core";
import { useCallback, useEffect, useLayoutEffect, useState } from "react";
import { FLEX_DEMO_STEPS } from "./flexDemo.const";
import { getFlexDemoCoachCopy, flexDemoStepIndex } from "./flexDemo.coach";
import { useFlexDemoSessionOptional } from "./FlexDemoSessionContext";

function queryTarget(id: string): Element | null {
  return document.querySelector(`[data-flex-demo-tour="${id}"]`);
}

function readRect(el: Element | null): DOMRect | null {
  if (!el) return null;
  return el.getBoundingClientRect();
}

export default function FlexDemoCoach() {
  const ctx = useFlexDemoSessionOptional();
  const [rect, setRect] = useState<DOMRect | null>(null);

  const step = ctx?.session.step;
  const copy = step
    ? getFlexDemoCoachCopy(
        step,
        Boolean(ctx?.session.collaborationsNavOpened),
        Boolean(ctx?.session.workspaceOpened),
      )
    : null;

  const updateRect = useCallback(() => {
    if (!copy?.spotlight) {
      setRect(null);
      return;
    }
    setRect(readRect(queryTarget(copy.spotlight)));
  }, [copy?.spotlight]);

  useLayoutEffect(() => {
    updateRect();
  }, [updateRect, step, copy?.spotlight]);

  useEffect(() => {
    const onWin = () => updateRect();
    window.addEventListener("resize", onWin);
    window.addEventListener("scroll", onWin, true);
    const t = window.setInterval(updateRect, 300);
    return () => {
      window.removeEventListener("resize", onWin);
      window.removeEventListener("scroll", onWin, true);
      window.clearInterval(t);
    };
  }, [updateRect]);

  if (!ctx || !copy) return null;

  const blocking = copy.blocking !== false;
  const stepIndex = flexDemoStepIndex(ctx.session.step);
  const pad = 8;
  const hole = blocking && rect && rect.width > 0
    ? {
        top: rect.top - pad,
        left: rect.left - pad,
        width: rect.width + pad * 2,
        height: rect.height + pad * 2,
      }
    : null;

  const cardW = 340;
  const gap = 16;
  let cardLeft = 16;
  let cardTop = 16;
  if (hole) {
    const rightEdge = hole.left + hole.width;
    const canPlaceRight = rightEdge + gap + cardW <= window.innerWidth - gap;
    const canPlaceLeft = hole.left - gap - cardW >= gap;
    cardLeft = canPlaceRight
      ? rightEdge + gap
      : canPlaceLeft
        ? hole.left - gap - cardW
        : Math.min(Math.max(gap, hole.left), window.innerWidth - cardW - gap);
    cardTop = Math.min(Math.max(gap, hole.top), window.innerHeight - 280);
  }

  if (!blocking) {
    return (
      <Paper
        shadow="md"
        p="md"
        radius="md"
        pos="fixed"
        maw={cardW}
        w={cardW}
        data-flex-demo-coach
        style={{
          bottom: 16,
          left: 16,
          zIndex: 180,
          pointerEvents: "auto",
          border: "1px solid rgba(148, 163, 184, 0.35)",
        }}
      >
        <Text size="xs" c="dimmed" fw={700} tt="uppercase" mb={6}>
          Demo · Step {stepIndex + 1} of {FLEX_DEMO_STEPS.length}
        </Text>
        <Text fw={800} size="lg" mb={4}>
          You are on: {copy.pageName}
        </Text>
        <Text size="sm" c="dimmed" mb="sm" style={{ lineHeight: 1.5 }}>
          {copy.pageIsFor}
        </Text>
        <Text size="sm" fw={700} c="primary.7">
          Next: {copy.nextClick}
        </Text>
      </Paper>
    );
  }

  return (
    <Box pos="fixed" inset={0} style={{ zIndex: 10040, pointerEvents: "none" }} data-flex-demo-coach>
      {hole ? (
        <>
          <Box
            pos="fixed"
            top={0}
            left={0}
            right={0}
            h={Math.max(0, hole.top)}
            bg="rgba(15, 23, 42, 0.55)"
            style={{ pointerEvents: "auto" }}
          />
          <Box
            pos="fixed"
            left={0}
            right={0}
            top={hole.top + hole.height}
            bottom={0}
            bg="rgba(15, 23, 42, 0.55)"
            style={{ pointerEvents: "auto" }}
          />
          <Box
            pos="fixed"
            left={0}
            w={Math.max(0, hole.left)}
            top={hole.top}
            h={hole.height}
            bg="rgba(15, 23, 42, 0.55)"
            style={{ pointerEvents: "auto" }}
          />
          <Box
            pos="fixed"
            left={hole.left + hole.width}
            right={0}
            top={hole.top}
            h={hole.height}
            bg="rgba(15, 23, 42, 0.55)"
            style={{ pointerEvents: "auto" }}
          />
          <Box
            pos="fixed"
            style={{
              top: hole.top,
              left: hole.left,
              width: hole.width,
              height: hole.height,
              borderRadius: 10,
              boxShadow: "0 0 0 3px #ffc300",
              pointerEvents: "none",
            }}
          />
        </>
      ) : (
        <Box pos="fixed" inset={0} bg="rgba(15, 23, 42, 0.25)" style={{ pointerEvents: "none" }} />
      )}

      <Paper
        shadow="md"
        p="md"
        radius="md"
        pos="fixed"
        maw={cardW}
        w={cardW}
        style={{
          top: cardTop,
          left: cardLeft,
          zIndex: 10052,
          pointerEvents: "auto",
          border: "1px solid rgba(148, 163, 184, 0.35)",
        }}
      >
        <Text size="xs" c="dimmed" fw={700} tt="uppercase" mb={6}>
          Demo · Step {stepIndex + 1} of {FLEX_DEMO_STEPS.length}
        </Text>
        <Text fw={800} size="lg" mb={4}>
          You are on: {copy.pageName}
        </Text>
        <Text size="sm" c="dimmed" mb="sm" style={{ lineHeight: 1.5 }}>
          {copy.pageIsFor}
        </Text>
        <Text size="sm" fw={700} c="primary.7">
          Next: {copy.nextClick}
        </Text>
      </Paper>
    </Box>
  );
}
